import React from "react"
import SanityImage from "gatsby-plugin-sanity-image"
import {
  RegBody,
  SanityImg,
  SemiBdTitle3,
  SemiBdTitle5,
} from "@sr/gatsby-theme-ui"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"
import { Link } from "gatsby"
import { useLocation } from "@reach/router"

const productHighlightsBg =
  "https://res.cloudinary.com/smartrent-com/image/upload/v1692347404/pages/product/ProductVector_cogvni.svg"

export interface ProductEntryProductHighlightsProps {
  productHighlights: {
    header: string
    body: string
    on: boolean
    img: SanityImg
    featureList: [
      {
        featureHeader: string
        body: string
      }
    ]
  }
}

const ProductEntryProductHighlights = ({
  productHighlights,
}: ProductEntryProductHighlightsProps) => {
  if (!productHighlights?.on) return null
  const { pathname } = useLocation()

  return (
    <ProductEntryProductHighlightsStyled>
      <div className="product-entry-product-highlights__container">
        <div className="product-entry-product-hightlights__intro-container">
          <SemiBdTitle3 className="product-entry-product-hightlights__intro-header">
            {productHighlights?.header && productHighlights.header}
          </SemiBdTitle3>
          <RegBody className="product-entry-product-hightlights__intro-body">
            {productHighlights?.body && productHighlights.body}
          </RegBody>
        </div>
        <div className="product-entry-product-hightlights__image-container">
          {productHighlights?.img?.asset && (
            <SanityImage
              alt={productHighlights?.img.alt || "Product Highlights"}
              asset={productHighlights?.img.asset}
              className="product-entry-product-hightlights__image"
              crop={productHighlights?.img.crop}
              hotspot={productHighlights?.img.hotspot}
              width={344}
              height={344}
            />
          )}
        </div>

        <div className="product-entry-product-hightlights__steps-container">
          {productHighlights.featureList.length
            ? productHighlights.featureList.map((feature: any, i: number) => {
                return (
                  <div
                    className="product-entry-product-hightlights__step"
                    key={i}
                  >
                    <div className="product-entry-product-hightlights__step-bullet-container">
                      <div className="product-entry-product-hightlights__step-bullet" />
                    </div>
                    <div className="product-entry-product-hightlights__step-text-container">
                      <SemiBdTitle5 className="product-entry-product-hightlights__step-header">
                        {feature.featureHeader}
                      </SemiBdTitle5>
                      <RegBody className="product-entry-product-hightlights__step-body">
                        {feature.body}{" "}
                        {pathname === "/products/community-wifi/" && i === 2 && <Link to="/products/community-wifi/noi-calculator/">See our NOI calculator</Link>}
                      </RegBody>
                    </div>
                  </div>
                )
              })
            : null}
        </div>
        <img
          className="product-entry-what-you-can-do-bg-img"
          src={productHighlightsBg}
        />
      </div>
    </ProductEntryProductHighlightsStyled>
  )
}

export default ProductEntryProductHighlights

const ProductEntryProductHighlightsStyled = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  background-color: var(--color-gray075);

  .product-entry-product-highlights__container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    text-align: center;
    padding: ${rem(80)} ${rem(16)} ${rem(104)};

    @media ${media.stMidUp} {
      width: ${rem(1440)};
      height: ${rem(912)};
      align-items: flex-end;
      text-align: left;
      padding: ${rem(80)} ${rem(140)} ${rem(96)};
    }
  }

  .product-entry-product-hightlights__intro-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${rem(16)};
    padding-bottom: ${rem(56)};
    max-width: ${rem(343)};

    @media ${media.midUp} {
      max-width: ${rem(595)};

      .product-entry-product-hightlights__intro-header {
        font-size: ${rem(40)};
        line-height: ${rem(48)};
      }

      .product-entry-product-hightlights__intro-body {
        max-width: ${rem(499)};
      }
    }

    @media ${media.stMidUp} {
      justify-content: flex-start;
      align-items: flex-start;
      width: 45%;
      padding-bottom: ${rem(40)};
    }
  }

  .product-entry-product-hightlights__image-container {
    padding-bottom: ${rem(80)};
    z-index: 2;
    background: transparent;

    @media ${media.midUp} {
      width: ${rem(488)};
      padding-bottom: ${rem(64)};
    }

    @media ${media.stMidUp} {
      position: absolute;
      left: calc(25% - 200px);
      top: calc(50% - 244px);
      padding-bottom: 0;
    }

    .product-entry-product-hightlights__image {
      border-radius: ${rem(245)};
      border: ${rem(3)} solid var(--color-gray900);
    }
  }

  .product-entry-product-hightlights__steps-container {
    display: flex;
    flex-direction: column;
    gap: ${rem(72)};

    @media ${media.stMidUp} {
      gap: ${rem(24)};
      width: 45%;
    }

    .product-entry-product-hightlights__step {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: ${rem(16)};

      @media ${media.stMidUp} {
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        gap: ${rem(24)};
      }

      .product-entry-product-hightlights__step-bullet {
        background-color: var(--color-primary);
        width: ${rem(16)};
        height: ${rem(16)};
        border-radius: ${rem(16)};

        @media ${media.stMidUp} {
          margin-top: 8px;
        }
      }

      .product-entry-product-hightlights__step-text-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: ${rem(8)};

        @media ${media.stMidUp} {
          align-items: flex-start;
        }

        .product-entry-product-hightlights__step-header {
          max-width: ${rem(272)};

          @media ${media.midUp} {
            font-size: ${rem(24)};
            max-width: ${rem(464)};
            line-height: ${rem(32)};
          }
        }

        .product-entry-product-hightlights__step-body {
          max-width: ${rem(343)};

          @media ${media.midUp} {
            max-width: ${rem(464)};
          }

          @media ${media.stMidUp} {
            max-width: ${rem(547)};
          }
        }
      }
    }
  }

  .product-entry-what-you-can-do-bg-img {
    display: none;

    @media ${media.midUp} {
      display: block;
      position: absolute;
      left: 0;
      top: ${rem(150)};
      z-index: 1;
    }

    @media ${media.stMidUp} {
      top: ${rem(150)};
    }
  }
`
